import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { useEmployees } from '@/hooks/use-employees';
import { useReportProjects } from '@/features/time-tracker/lib/time-tracking-projects';
import {
  useDepartments,
  useDivisions,
  useEmploymentTypes,
  useLocations,
  usePositions,
  useSeniorityLevels,
} from '@/hooks/use-organizations';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { projectManagerOptions } from '../lib/project-managers';
import { projectOptions } from '../lib/project-options';
import type { FilterOption, FilterOptionsSource } from '../types';

// ============================================================================
// Option Source Registry
// ============================================================================

/**
 * Registry of predefined option sources.
 * Each source defines how to fetch and transform data into FilterOption[].
 */
export const OPTION_SOURCES = {
  /** Departments with employee counts */
  departments: {
    hook: useDepartments,
    transform: (data: unknown): FilterOption[] => {
      if (!Array.isArray(data)) return [];
      return data.map((d) => ({
        value: d.id,
        label: d.name,
        count: d._count?.employees,
      }));
    },
  },

  /** Positions with employee counts */
  positions: {
    hook: usePositions,
    transform: (data: unknown): FilterOption[] => {
      if (!Array.isArray(data)) return [];
      return data.map((p) => ({
        value: p.id,
        label: p.title,
        count: p._count?.employees,
      }));
    },
  },

  /** Seniority levels with employee counts */
  seniorityLevels: {
    hook: useSeniorityLevels,
    transform: (data: unknown): FilterOption[] => {
      if (!Array.isArray(data)) return [];
      return data.map((l) => ({
        value: l.id,
        label: l.name,
        count: l._count?.employees,
      }));
    },
  },

  /** Divisions with employee counts */
  divisions: {
    hook: useDivisions,
    transform: (data: unknown): FilterOption[] => {
      if (!Array.isArray(data)) return [];
      return data.map((d) => ({
        value: d.id,
        label: d.name,
        count: d._count?.employees,
      }));
    },
  },

  /** Locations with employee counts */
  locations: {
    hook: useLocations,
    transform: (data: unknown): FilterOption[] => {
      if (!Array.isArray(data)) return [];
      return data.map((l) => ({
        value: l.id,
        label: l.name,
        description: l.city ? `${l.city}, ${l.country}` : l.country,
        count: l._count?.employees,
      }));
    },
  },

  /** Employment types with employee counts */
  employmentTypes: {
    hook: useEmploymentTypes,
    transform: (data: unknown): FilterOption[] => {
      if (!Array.isArray(data)) return [];
      return data.map((t) => ({
        value: t.id,
        label: t.name,
        count: t._count?.employees,
      }));
    },
  },

  /**
   * Projects, by name.
   *
   * Every status is offered, archived included: an archived project still holds
   * the hours somebody logged against it, so a report about it is a fair
   * question — and the builder has a separate status filter for narrowing. The
   * inactive ones are flagged, so the multi-select lists them only behind its
   * "Show archived" toggle (`project-options.ts`). [HW-951]
   *
   * `PROJECT_FILTER_QUERY` carries the page size, and its limit is the ceiling
   * rather than a preference: `/projects` validates its query against
   * `PaginationQueryDto`, whose `@Max(100)` would refuse the 200 the employee
   * sources ask for. (Those reach a controller that takes a raw record instead
   * of the DTO, which is why they get away with it.) [HW-903]
   *
   * Read through the Time Tracker's `GET /time-entries/report-projects`, which
   * takes the same query: `/projects` narrows a role holding only
   * `projects.view.assigned` to its memberships, while a holder of
   * `time.reports.all` or `time.edit.all` reads every project's hours here.
   * The Reports and Dashboard Project filters read the same hook, so the
   * Builder and the screens beside it answer from one fetch. [HW-972]
   */
  projects: {
    hook: useReportProjects,
    transform: projectOptions,
  },

  /**
   * People who actually manage a project, from the projects list's own
   * `managers[]` — the `isManager` memberships, which is what a project-manager
   * predicate tests. See `lib/project-managers.ts` for why this is not
   * `managers` below.
   *
   * The same hook as `projects` above, deliberately: one query key, so the two
   * dedupe onto one request and a screen offering both pays for one — and a
   * reader offered every project is offered every one of their managers.
   * [HW-972]
   */
  projectManagers: {
    hook: useReportProjects,
    transform: (data: unknown): FilterOption[] => projectManagerOptions(data),
  },

  /** Active employees (for manager selection) */
  managers: {
    hook: () => useEmployees({ status: 'active', limit: 100 }),
    transform: (data: unknown): FilterOption[] => {
      const employeeData = data as
        | {
            data?: Array<{
              id: string;
              firstName: string;
              lastName: string;
              avatarUrl?: string;
              position?: { title: string } | null;
            }>;
          }
        | undefined;

      if (!employeeData?.data) return [];
      return employeeData.data.map((e) => ({
        value: e.id,
        label: `${e.firstName} ${e.lastName}`,
        avatar: e.avatarUrl,
        description: e.position?.title,
      }));
    },
  },

  /** All employees (for assignment selection) */
  employees: {
    hook: () => useEmployees({ status: 'active', limit: 200 }),
    transform: (data: unknown): FilterOption[] => {
      const employeeData = data as
        | {
            data?: Array<{
              id: string;
              firstName: string;
              lastName: string;
              avatarUrl?: string;
              workEmail: string;
              department?: { name: string } | null;
            }>;
          }
        | undefined;

      if (!employeeData?.data) return [];
      return employeeData.data.map((e) => ({
        value: e.id,
        label: `${e.firstName} ${e.lastName}`,
        avatar: e.avatarUrl,
        description: e.department?.name ?? e.workEmail,
      }));
    },
  },
} as const;

/** Type for predefined source keys */
export type OptionSourceKey = keyof typeof OPTION_SOURCES;

// ============================================================================
// Hook Result
// ============================================================================

interface UseFilterOptionsResult {
  /** Array of filter options */
  options: FilterOption[];
  /** Whether options are loading */
  isLoading: boolean;
  /** Error if loading failed */
  error?: Error;
}

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Hook to load filter options from various sources.
 * Supports:
 * - Static arrays
 * - Predefined source keys (departments, positions, etc.)
 * - Custom hooks
 * - React Query configurations
 *
 * @example With predefined source
 * ```tsx
 * const { options, isLoading } = useFilterOptions('departments');
 * ```
 *
 * @example With static array
 * ```tsx
 * const { options } = useFilterOptions([
 *   { value: 'active', label: 'Active' },
 *   { value: 'inactive', label: 'Inactive' },
 * ]);
 * ```
 *
 * @example With custom transform
 * ```tsx
 * const { options } = useFilterOptions('departments', {
 *   transform: (data) => data.map(d => ({ value: d.id, label: d.name.toUpperCase() }))
 * });
 * ```
 */
export function useFilterOptions(
  source: FilterOptionsSource | undefined,
  options?: {
    /** Custom transform function to override default */
    transform?: (data: unknown) => FilterOption[];
    /** Whether to enable the query (for conditional loading) */
    enabled?: boolean;
  }
): UseFilterOptionsResult {
  const { transform: customTransform, enabled = true } = options ?? {};
  const formatName = useEmployeeNameFormatter();

  // =========================================================================
  // Handle predefined source keys
  // =========================================================================

  const isPredefinedKey = typeof source === 'string' && source in OPTION_SOURCES;
  const sourceConfig = isPredefinedKey ? OPTION_SOURCES[source as OptionSourceKey] : null;

  // Call the hook from predefined source (hooks must be called unconditionally)
  const predefinedResult = sourceConfig?.hook?.() ?? { data: undefined, isLoading: false };

  // =========================================================================
  // Handle hook-based sources
  // =========================================================================

  const isHookSource = source && typeof source === 'object' && 'hook' in source;
  const hookResult = isHookSource ? source.hook() : { data: undefined, isLoading: false };

  // =========================================================================
  // Handle query-based sources
  // =========================================================================

  const isQuerySource = source && typeof source === 'object' && 'queryKey' in source;
  const queryResult = useQuery({
    queryKey: isQuerySource ? source.queryKey : ['__disabled__'],
    queryFn: isQuerySource ? source.queryFn : () => Promise.resolve([]),
    enabled: Boolean(enabled && isQuerySource),
  });

  // =========================================================================
  // Compute final options
  // =========================================================================

  const result = useMemo<UseFilterOptionsResult>(() => {
    // Static array
    if (Array.isArray(source)) {
      return { options: source, isLoading: false };
    }

    // Predefined source
    if (isPredefinedKey && sourceConfig) {
      let transform = customTransform ?? sourceConfig.transform;
      // People are named the way this company names people, wherever they come
      // from — the registry's transforms cannot call a hook, so the formatter
      // is applied here.
      if (!customTransform && source === 'projectManagers') {
        transform = (data: unknown): FilterOption[] =>
          projectManagerOptions(data, (person) => formatName(person));
      }
      if (!customTransform && (source === 'managers' || source === 'employees')) {
        transform = (data: unknown): FilterOption[] => {
          const employeeData = data as
            | { data?: Array<{ id: string; firstName: string; lastName: string; avatarUrl?: string; position?: { title: string } | null; department?: { name: string } | null; workEmail?: string }> }
            | undefined;
          if (!employeeData?.data) return [];
          return employeeData.data.map((e) => ({
            value: e.id,
            label: formatName({ firstName: e.firstName, lastName: e.lastName }),
            avatar: e.avatarUrl,
            description: source === 'employees' ? (e.department?.name ?? e.workEmail) : e.position?.title,
          }));
        };
      }
      return {
        options: transform(predefinedResult.data),
        isLoading: predefinedResult.isLoading,
      };
    }

    // Hook-based source
    if (isHookSource) {
      const transform = customTransform ?? ((data: unknown) => (data as FilterOption[]) ?? []);
      return {
        options: transform(hookResult.data),
        isLoading: hookResult.isLoading,
      };
    }

    // Query-based source
    if (isQuerySource) {
      const transform = customTransform ?? ((data: unknown) => (data as FilterOption[]) ?? []);
      return {
        options: transform(queryResult.data),
        isLoading: queryResult.isLoading,
        error: queryResult.error ?? undefined,
      };
    }

    // No source or undefined
    return { options: [], isLoading: false };
  }, [
    source,
    isPredefinedKey,
    sourceConfig,
    predefinedResult.data,
    predefinedResult.isLoading,
    isHookSource,
    hookResult.data,
    hookResult.isLoading,
    isQuerySource,
    queryResult.data,
    queryResult.isLoading,
    queryResult.error,
    customTransform,
    formatName,
  ]);

  return result;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if a source key is a predefined option source.
 */
export function isPredefinedSource(source: unknown): source is OptionSourceKey {
  return typeof source === 'string' && source in OPTION_SOURCES;
}

/**
 * Get all available predefined source keys.
 */
export function getAvailableSources(): OptionSourceKey[] {
  return Object.keys(OPTION_SOURCES) as OptionSourceKey[];
}
